import moment from "moment"
import AirDatepicker from "air-datepicker"
import localeEn from "../air-datepicker/en"

const CLOSED_PARK_MONTHS = [10, 11, 0, 1]
const DATE_FLEXIBILITY_PATTERN = /^plus_minus_(\d+)$/

class HolidayParkAccommodationCalendar {
  constructor(container) {
    this.container = container
    this.form = container.querySelector("form")
    this.toggleButton = container.querySelector("[data-accommodation-search-toggle]")
    this.closeButton = container.querySelector("[data-accommodation-search-close]")
    this.backdrop = container.querySelector("[data-accommodation-search-backdrop]")
    this.dock = container.querySelector("[data-accommodation-search-dock]")
    this.searchPanel = container.querySelector("[data-accommodation-search-panel]")
    this.calendar = container.querySelector("[data-accommodation-calendar]")
    this.arrivalDateInput = container.querySelector("[data-accommodation-calendar-input]")
    this.nightsSelect = container.querySelector("[data-accommodation-nights-select]")
    this.dateFlexibilitySelect = container.querySelector('[name="date_flexibility"]')
    this.adultsSelect = container.querySelector('[name="no_of_adults"]')
    this.childrenSelect = container.querySelector('[name="no_of_children"]')
    this.dogsSelect = container.querySelector('[name="no_of_pets"]')
    this.dockSummary = container.querySelector("[data-accommodation-search-dock-summary]")
    this.selectedDateSummary = container.querySelector("[data-accommodation-calendar-selected-date]")
    this.staySummary = container.querySelector("[data-accommodation-calendar-stay]")
    this.calendarPlaceholder = container.querySelector("[data-accommodation-calendar-placeholder]")
    this.datepicker = null
    this.bodyClass = "has-accommodation-booking-dock"
    this.bodyOpenClass = "has-accommodation-booking-panel-open"
    this.hasUserInteracted = this.searchPanel?.dataset.initiallyOpen === "true"
    this.lastFocusedElement = null
    this.focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    this.handleContainerClick = (event) => this.onContainerClick(event)
    this.handleDocumentClick = (event) => this.onDocumentClick(event)
    this.handleDocumentKeydown = (event) => this.onDocumentKeydown(event)
    this.handleCalendarKeydown = (event) => this.onCalendarKeydown(event)
    this.handleCalendarClick = () => window.requestAnimationFrame(() => this.decorateCalendarAccessibility())

    if (!this.form || !this.calendar || !this.arrivalDateInput || !this.nightsSelect || !this.searchPanel) {
      return
    }

    this.bindEvents()
    this.initializeToggle()
    this.initializeCalendar()
  }

  bindEvents() {
    this.nightsSelect.addEventListener("change", () => this.handleFieldInteraction())
    this.dateFlexibilitySelect?.addEventListener("change", () => this.handleFieldInteraction())
    this.adultsSelect?.addEventListener("change", () => this.handleFieldInteraction())
    this.childrenSelect?.addEventListener("change", () => this.handleFieldInteraction())
    this.dogsSelect?.addEventListener("change", () => this.handleFieldInteraction())
    this.container.addEventListener("click", this.handleContainerClick)
    document.addEventListener("click", this.handleDocumentClick, true)
    this.form.addEventListener("submit", () => {
      this.hasUserInteracted = true
      this.updateSummary()
      this.setPanelVisibility(true)
    })
    this.calendar.addEventListener("keydown", this.handleCalendarKeydown)
    this.calendar.addEventListener("click", this.handleCalendarClick)
    document.addEventListener("keydown", this.handleDocumentKeydown)
  }

  initializeCalendar() {
    const initialDate = this.resolveInitialDate()

    this.datepicker = new AirDatepicker(this.calendar, {
      inline: true,
      locale: localeEn,
      minDate: this.today(),
      selectedDates: [initialDate],
      startDate: initialDate,
      onRenderCell: ({ date, cellType }) => this.renderCalendarCell(date, cellType),
      onSelect: ({ date }) => this.handleDateSelection(date),
    })

    this.handleDateSelection(initialDate, { markInteracted: false })
    this.decorateCalendarAccessibility()
  }

  initializeToggle() {
    const initiallyOpen = this.searchPanel.dataset.initiallyOpen === "true"

    if (this.currentDock()) {
      document.body.classList.add(this.bodyClass)
    }
    this.container.dataset.holidayParkAccommodationToggleReady = "true"
    this.setPanelVisibility(initiallyOpen)
  }

  setPanelVisibility(isVisible) {
    const searchPanel = this.currentSearchPanel()
    const backdrop = this.currentBackdrop()
    const toggleButton = this.currentToggleButton()
    const dock = this.currentDock()

    if (!searchPanel) {
      return
    }

    if (isVisible) {
      this.lastFocusedElement = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    }

    searchPanel.hidden = !isVisible
    searchPanel.style.display = isVisible ? "block" : "none"
    searchPanel.setAttribute("aria-hidden", String(!isVisible))
    if (backdrop) {
      backdrop.hidden = !isVisible
      backdrop.style.display = isVisible ? "block" : "none"
    }
    if (dock) {
      dock.setAttribute("aria-hidden", String(isVisible))
      if ("inert" in dock) {
        dock.inert = isVisible
      }
    }
    this.container.classList.toggle("is-booking-panel-collapsed", !isVisible)
    document.body.classList.toggle(this.bodyOpenClass, isVisible)

    if (toggleButton) {
      const openLabel = toggleButton.dataset.openLabel ?? "Hide booking calendar"
      const closedLabel = toggleButton.dataset.closedLabel ?? "Show booking calendar"

      toggleButton.setAttribute("aria-expanded", String(isVisible))
      toggleButton.textContent = isVisible ? openLabel : closedLabel
    }

    if (isVisible) {
      window.requestAnimationFrame(() => this.focusFirstPanelControl())
      return
    }

    this.restoreFocus()
  }

  renderCalendarCell(date, cellType) {
    if (cellType !== "day") {
      return undefined
    }

    const today = this.today()

    if (date < today || this.isParkClosed(date)) {
      const unavailableReason = this.isParkClosed(date)
        ? "The park is closed from November to February"
        : "Check-in is not available before today"

      return {
        disabled: true,
        classes: "disabled-class",
        attrs: {
          title: unavailableReason,
          "aria-label": `Unavailable check-in on ${moment(date).format("dddd D MMMM YYYY")}`,
          "aria-disabled": "true",
          tabindex: "-1",
          role: "gridcell",
        },
      }
    }

    return {
      attrs: {
        title: `Available check-in on ${moment(date).format("dddd D MMMM YYYY")}`,
        "aria-label": `Available check-in on ${moment(date).format("dddd D MMMM YYYY")}`,
        "aria-disabled": "false",
        tabindex: "-1",
        role: "button",
      },
    }
  }

  handleFieldInteraction() {
    this.hasUserInteracted = true
    this.updateSummary()
  }

  handleDateSelection(date, options = {}) {
    const { markInteracted = true } = options
    const selectedDate = this.normalizeArrivalDate(date)

    if (!selectedDate) {
      return
    }

    if (markInteracted) {
      this.hasUserInteracted = true
    }

    this.arrivalDateInput.value = moment(selectedDate).format("YYYY-MM-DD")
    this.syncStayLengthOptions(selectedDate)
    this.updateSummary(selectedDate)
    window.requestAnimationFrame(() => this.decorateCalendarAccessibility())
  }

  syncStayLengthOptions(selectedDate) {
    const selectedNights = parseInt(this.nightsSelect.value, 10)

    if (!Number.isNaN(selectedNights)) {
      this.nightsSelect.value = String(Math.min(21, Math.max(2, selectedNights)))
    }
  }

  updateSummary(selectedDate = null) {
    const arrivalDate = selectedDate ?? this.normalizeArrivalDate(this.arrivalDateInput.value)

    if (!arrivalDate) {
      this.renderUnselectedCalendarSummary()
      this.updateDockSummary()
      return
    }

    const numberOfNights = parseInt(this.nightsSelect.value, 10) || 3
    const departureDate = moment(arrivalDate).add(numberOfNights, "days")
    const flexibilityDays = this.flexibilityDays()
    const flexibilitySuffix = flexibilityDays
      ? ` with +/- ${flexibilityDays} days flexibility`
      : ""

    if (!this.hasUserInteracted) {
      this.renderUnselectedCalendarSummary()
      this.updateDockSummary()
      return
    }

    this.selectedDateSummary.textContent = `Arrival: ${moment(arrivalDate).format("dddd D MMMM YYYY")}`
    this.staySummary.textContent = `${numberOfNights} night stay, departing ${departureDate.format("dddd D MMMM YYYY")}${flexibilitySuffix}.`
    this.selectedDateSummary.hidden = false
    this.staySummary.hidden = false
    this.calendarPlaceholder?.setAttribute("hidden", "hidden")

    if (this.hasUserInteracted) {
      this.updateDockSummary(arrivalDate, numberOfNights)
      return
    }

    this.updateDockSummary()
  }

  flexibilityDays() {
    const value = this.dateFlexibilitySelect?.value
    const matches = typeof value === "string" ? value.match(DATE_FLEXIBILITY_PATTERN) : null

    return matches ? parseInt(matches[1], 10) : null
  }

  updateDockSummary(arrivalDate = null, numberOfNights = null) {
    if (!this.dockSummary) {
      return
    }

    if (!this.hasUserInteracted) {
      this.dockSummary.textContent = "Choose your arrival date, stay length and guests."
      return
    }

    const summaryParts = []
    const adults = parseInt(this.adultsSelect?.value ?? "1", 10) || 1
    const children = parseInt(this.childrenSelect?.value ?? "0", 10) || 0
    const dogs = parseInt(this.dogsSelect?.value ?? "0", 10) || 0

    if (arrivalDate) {
      summaryParts.push(moment(arrivalDate).format("ddd D MMM"))
    }

    if (numberOfNights) {
      summaryParts.push(`${numberOfNights} night${numberOfNights === 1 ? "" : "s"}`)
    }

    summaryParts.push(`${adults} adult${adults === 1 ? "" : "s"}`)

    if (children > 0) {
      summaryParts.push(`${children} ${children === 1 ? "child" : "children"}`)
    }

    if (dogs > 0) {
      summaryParts.push(`${dogs} dog${dogs === 1 ? "" : "s"}`)
    }

    this.dockSummary.textContent = summaryParts.join(" • ")
  }

  renderUnselectedCalendarSummary() {
    if (this.selectedDateSummary) {
      this.selectedDateSummary.textContent = ""
      this.selectedDateSummary.hidden = true
    }

    if (this.staySummary) {
      this.staySummary.textContent = ""
      this.staySummary.hidden = true
    }

    this.calendarPlaceholder?.removeAttribute("hidden")
  }

  onContainerClick(event) {
    const toggleButton = event.target.closest("[data-accommodation-search-toggle]")
    if (toggleButton) {
      event.preventDefault()
      this.setPanelVisibility(this.currentSearchPanel()?.hidden ?? true)
      return
    }

    if (event.target.closest("[data-accommodation-search-close]")) {
      event.preventDefault()
      this.setPanelVisibility(false)
      return
    }

    if (event.target.closest("[data-accommodation-search-backdrop]")) {
      event.preventDefault()
      this.setPanelVisibility(false)
      return
    }

    if (event.target === this.currentSearchPanel()) {
      this.setPanelVisibility(false)
    }
  }

  onDocumentClick(event) {
    const link = this.bookingPanelTriggerLink(event)

    if (!link) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    this.setPanelVisibility(true)
  }

  bookingPanelTriggerLink(event) {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return null
    }

    if (!(event.target instanceof Element)) {
      return null
    }

    const link = event.target.closest("a")

    if (!link || this.container.contains(link)) {
      return null
    }

    if (link.hasAttribute("download") || link.dataset.ignoreAccommodationSearchOpen === "true") {
      return null
    }

    if (link.target && link.target.toLowerCase() !== "_self") {
      return null
    }

    if (
      link.dataset.accommodationSearchOpen === "true"
      || link.dataset.holidayParkBookingOpen === "true"
    ) {
      return link
    }

    const label = link.textContent?.replace(/\s+/g, " ").trim().toLowerCase()

    return label === "book now" ? link : null
  }

  onDocumentKeydown(event) {
    if (this.currentSearchPanel()?.hidden ?? true) {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.setPanelVisibility(false)
      return
    }

    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  trapFocus(event) {
    const searchPanel = this.currentSearchPanel()
    const focusableElements = this.getFocusableElements()

    if (!searchPanel || focusableElements.length === 0) {
      return
    }

    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]
    const activeElement = document.activeElement

    if (event.shiftKey) {
      if (activeElement === firstFocusable || !searchPanel.contains(activeElement)) {
        event.preventDefault()
        lastFocusable.focus()
      }

      return
    }

    if (activeElement === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  getFocusableElements() {
    const searchPanel = this.currentSearchPanel()

    if (!searchPanel) {
      return []
    }

    return Array.from(searchPanel.querySelectorAll(this.focusableSelector))
      .filter((element) => element instanceof HTMLElement)
      .filter((element) => !element.hasAttribute("hidden") && element.getClientRects().length > 0)
  }

  focusFirstPanelControl() {
    const focusTarget = this.currentCloseButton()
      ?? this.getFocusableElements()[0]

    focusTarget?.focus()
  }

  restoreFocus() {
    const fallbackTarget = this.currentToggleButton()
    const previousTarget = this.lastFocusedElement

    if (previousTarget instanceof HTMLElement && previousTarget.isConnected) {
      previousTarget.focus()
      return
    }

    fallbackTarget?.focus()
  }

  onCalendarKeydown(event) {
    const cell = event.target.closest(".air-datepicker-cell")

    if (!cell || cell.classList.contains("-disabled-")) {
      return
    }

    const navigationOffsets = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      cell.click()
      return
    }

    if (!(event.key in navigationOffsets)) {
      return
    }

    event.preventDefault()
    this.moveCalendarFocus(cell, navigationOffsets[event.key])
  }

  moveCalendarFocus(currentCell, offset) {
    const cells = Array.from(this.calendar.querySelectorAll(".air-datepicker-cell"))
    const currentIndex = cells.indexOf(currentCell)

    if (currentIndex < 0) {
      return
    }

    let nextIndex = currentIndex + offset

    while (nextIndex >= 0 && nextIndex < cells.length) {
      const candidate = cells[nextIndex]

      if (!candidate.classList.contains("-disabled-")) {
        this.setCalendarCellTabStops(candidate)
        candidate.focus()
        return
      }

      nextIndex += offset > 0 ? 1 : -1
    }
  }

  decorateCalendarAccessibility() {
    const cells = Array.from(this.calendar.querySelectorAll(".air-datepicker-cell"))
    const selectedCell = cells.find((cell) => cell.classList.contains("-selected-"))
    const activeCell = selectedCell
      ?? cells.find((cell) => !cell.classList.contains("-disabled-"))
      ?? null

    cells.forEach((cell) => {
      const isoDate = cell.dataset.isoDate
      const formattedDate = isoDate
        ? moment(isoDate, "YYYY-MM-DD").format("dddd D MMMM YYYY")
        : cell.textContent?.trim() ?? "Unavailable date"
      const isDisabled = cell.classList.contains("-disabled-")
      const isSelected = cell.classList.contains("-selected-")

      cell.setAttribute("aria-label", `${isDisabled ? "Unavailable" : "Available"} check-in on ${formattedDate}`)
      cell.setAttribute("aria-disabled", String(isDisabled))
      cell.setAttribute("aria-pressed", String(!isDisabled && isSelected))
      cell.setAttribute("role", isDisabled ? "gridcell" : "button")
    })

    this.setCalendarCellTabStops(activeCell)
  }

  setCalendarCellTabStops(activeCell) {
    const cells = Array.from(this.calendar.querySelectorAll(".air-datepicker-cell"))

    cells.forEach((cell) => {
      if (cell.classList.contains("-disabled-")) {
        cell.setAttribute("tabindex", "-1")
        return
      }

      cell.setAttribute("tabindex", cell === activeCell ? "0" : "-1")
    })
  }

  resolveInitialDate() {
    const requestedDate = this.normalizeArrivalDate(this.arrivalDateInput.value)

    if (requestedDate) {
      return requestedDate
    }

    return this.nextAllowedArrivalDate(this.today())
  }

  normalizeArrivalDate(date) {
    const parsedDate = this.parseDate(date)

    if (!parsedDate) {
      return null
    }

    return this.nextAllowedArrivalDate(parsedDate.toDate())
  }

  parseDate(date) {
    if (!date) {
      return null
    }

    const parsedDate = moment(date, ["YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YY", "DD-MM-YYYY"], true)

    if (!parsedDate.isValid()) {
      return null
    }

    return parsedDate.startOf("day")
  }

  nextAllowedArrivalDate(date) {
    const candidate = moment(date).startOf("day")
    const today = moment(this.today())

    if (candidate.isBefore(today)) {
      candidate.year(today.year()).month(today.month()).date(today.date())
    }

    while (this.isParkClosed(candidate)) {
      candidate.add(1, "day")
    }

    return candidate.toDate()
  }

  today() {
    return moment().startOf("day").toDate()
  }

  isParkClosed(date) {
    return CLOSED_PARK_MONTHS.includes(moment(date).month())
  }

  currentToggleButton() {
    return this.container.querySelector("[data-accommodation-search-toggle]") ?? this.toggleButton
  }

  currentCloseButton() {
    return this.container.querySelector("[data-accommodation-search-close]") ?? this.closeButton
  }

  currentDock() {
    return this.container.querySelector("[data-accommodation-search-dock]") ?? this.dock
  }

  currentSearchPanel() {
    return this.container.querySelector("[data-accommodation-search-panel]") ?? this.searchPanel
  }

  currentBackdrop() {
    return this.container.querySelector("[data-accommodation-search-backdrop]") ?? this.backdrop
  }
}

export default function initHolidayParkAccommodationCalendar() {
  document.querySelectorAll("[data-holiday-park-accommodation-search], [data-holiday-park-global-booking-search]").forEach((container) => {
    if (container.dataset.holidayParkAccommodationCalendarInitialized === "true") {
      return
    }

    container.dataset.holidayParkAccommodationCalendarInitialized = "true"
    new HolidayParkAccommodationCalendar(container)
  })
}
